/* -----------------Globals--------------- */
import { isAndroid, isWeb } from "@memorang/helpers/platform";

import Icon from "@react-native-vector-icons/material-design-icons/static";
/* -----------------UI--------------- */
import { useTranslation } from "react-i18next";
import {
	Card,
	Checkbox,
	IconButton,
	List,
	RadioButton,
	Text,
} from "react-native-paper";
import { themeRoundnessMultiplier } from "../../../constants";

/* -----------------Helpers & Hooks--------------- */
import { useAppTheme } from "../../../useAppTheme";

import type { Media } from "@memorang/types";
/* -----------------Child components--------------- */
import type { TextStyle, ViewStyle } from "react-native";
import { useAppConfig } from "../../../contexts/AppConfigContext";
import Box from "../../Box";
import ImagesCarousel from "../../ImagesCarousel";
import { ListItemContainer } from "../../ListItemContainer";
import RenderMarkdown from "../../MarkdownRenderer";
import TooltipWrapper from "../../TooltipWrapper";

type Props = {
	answered?: boolean;
	text: string;
	handleSelectChoice: (crossingOut?: boolean) => void;
	status: "checked" | "unchecked";
	multiSelect?: boolean;
	value?: string;
	isCrossedOut?: boolean;
	isCorrectAnswer?: boolean;
	percentageAnswered?: number;
	useCardWrapper?: boolean;
	showCrossOut?: boolean;
	isSelected?: boolean;
	index: number;
	drag?: () => void;
	disabled?: boolean;
	fontSize?: number;
	media?: Media[];
	isSummativeTest?: boolean;
};
const ChoiceListItem = ({
	isSummativeTest,
	answered,
	disabled,
	text,
	handleSelectChoice,
	status,
	multiSelect,
	value,
	isCrossedOut,
	isCorrectAnswer,
	percentageAnswered,
	showCrossOut,
	isSelected = false,
	index,
	drag,
	fontSize,
	media,
}: Props) => {
	const theme = useAppTheme();
	const { appFont = "opensans" } = useAppConfig();
	const { t } = useTranslation();

	const SelectionContainer = multiSelect
		? Checkbox.Android
		: RadioButton.Android;
	const showLeft = !drag;
	const showAnswerIcon = !!answered;

	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: na
	const getCardProps = () => {
		if (answered) {
			if (isCorrectAnswer) {
				return {
					backgroundColor: theme.palette.answer.correct.background,
					borderColor: "transparent",
					borderWidth: 1,
				};
			}
			if (isSelected) {
				return {
					backgroundColor: theme.palette.answer.incorrect.background,
					borderColor: theme.colors.error,
					borderWidth: 1.5,
				};
			}
			return {
				backgroundColor: isSummativeTest
					? theme.dark
						? theme.colors.background
						: theme.colors.white
					: theme.colors.surface,
				borderColor: "transparent",
				borderWidth: 1,
			};
		}

		if (isSelected) {
			return {
				backgroundColor: theme.colors.surface,
				borderColor: theme.colors.primary,
				borderWidth: 1,
			};
		}

		return {
			backgroundColor: isSummativeTest
				? theme.dark
					? theme.colors.background
					: //TODO:Need to refactor the theme colors for some of the apps
						theme.colors.white
				: theme.colors.surface,
			borderColor: theme.colors.outlineVariant,
			borderWidth: 1,
		};
	};

	const CrossOutAnswerIconButton = (props: {
		color: string;
		style?: ViewStyle | undefined;
		isCrossedOut?: boolean;
	}) => (
		<IconButton
			{...props}
			disabled={answered || disabled}
			onPress={() => {
				if (!answered) {
					handleSelectChoice(true);
				}
			}}
			iconColor={theme.colors.onSurfaceVariant}
			icon={(props) => <Icon {...props} name="close" />}
			size={24}
			testID={`cross-out-answer-icon-button-${index}`}
			style={{
				marginRight: -6,
				...(isCrossedOut ? { opacity: 0.38 } : {}),
			}}
		/>
	);

	const AnswerStatusIcon = () => {
		if (!(isCorrectAnswer || isSelected)) {
			return (
				<Box
					width={36}
					height={36}
					alignItems="center"
					justifyContent="center"
					accessibilityLabel={t("session.choiceNotSelectedA11yLabel")}
				>
					<Icon
						name="minus-circle-outline"
						color={theme.colors.onSurfaceVariant}
						size={24}
					/>
				</Box>
			);
		}
		return (
			<Box
				width={36}
				height={36}
				alignItems="center"
				justifyContent="center"
				accessibilityLabel={
					isCorrectAnswer
						? t("session.choiceCorrectA11yLabel")
						: t("session.choiceIncorrectA11yLabel")
				}
			>
				<Icon
					name={
						isCorrectAnswer ? "checkbox-marked-circle-outline" : "close-circle"
					}
					color={
						isCorrectAnswer
							? theme.palette.answer.correct.border
							: theme.palette.answer.incorrect.border
					}
					size={24}
				/>
			</Box>
		);
	};

	const PercentageAnsweredChip = ({ value }: { value: number }) => {
		const percentLabel = value < 1 ? "< 1%" : `${Math.round(value)}%`;
		const backgroundColor = isCorrectAnswer
			? theme.palette.answer.correct.background
			: isSelected
				? theme.palette.answer.incorrect.background
				: theme.colors.surfaceVariant;
		return (
			<TooltipWrapper
				title={t("session.choicePercentageTooltip", {
					percentage: percentLabel,
				})}
			>
				<Box
					backgroundColor={backgroundColor}
					borderRadius={theme.roundness}
					paddingHorizontal={5}
					paddingVertical={2}
				>
					<Text
						variant="labelMedium"
						style={{
							color: theme.colors.onSurfaceVariant,
							fontFamily: `${appFont}-semibold`,
						}}
					>
						{percentLabel}
					</Text>
				</Box>
			</TooltipWrapper>
		);
	};

	const cardProps = getCardProps();
	const itemCursor = disabled ? "not-allowed" : answered ? "auto" : "pointer";
	const showStatChip = answered && percentageAnswered != null;

	const DragStatusIcon = () => {
		if (answered) {
			return (
				<Icon
					name={
						isCorrectAnswer ? "check-circle-outline" : "close-circle-outline"
					}
					color={
						isCorrectAnswer
							? theme.palette.success.main
							: theme.palette.confidence.low
					}
					size={24}
				/>
			);
		}
		return (
			<Icon
				name="drag-vertical"
				color={theme.colors.onSurfaceVariant}
				size={24}
				style={
					isWeb
						? ({
								cursor: disabled ? "not-allowed" : "grab",
							} as unknown as TextStyle)
						: undefined
				}
			/>
		);
	};

	return (
		<Card
			mode="contained"
			style={{
				...cardProps,
				opacity: disabled && !answered ? 0.5 : 1,
				overflow: "hidden",
				borderRadius: theme.roundness * themeRoundnessMultiplier,
			}}
		>
			<ListItemContainer
				handleCrossOut={() => {
					if (!(answered || disabled)) {
						handleSelectChoice(true);
					}
				}}
			>
				<List.Item
					onLongPress={drag}
					delayLongPress={drag ? (isAndroid ? 180 : 300) : 500}
					disabled={answered || disabled}
					accessibilityLabel={`Choice ${index}. ${text}`}
					accessible
					style={{
						paddingLeft: 8,
						paddingRight: 12,
						paddingVertical: isSummativeTest ? 0 : 8,
						...(isWeb ? ({ cursor: itemCursor } as ViewStyle) : {}),
					}}
					contentStyle={{ paddingLeft: 8 }}
					left={(p) =>
						showLeft ? (
							<Box
								flexDirection="row"
								alignItems="center"
								style={
									isWeb ? ({ cursor: itemCursor } as ViewStyle) : undefined
								}
							>
								{showAnswerIcon ? (
									<AnswerStatusIcon />
								) : (
									<SelectionContainer
										{...p}
										value={value!}
										status={status}
										color={
											cardProps.borderColor || theme.colors.onSurfaceVariant
										}
										accessibilityLabel={`Choice ${index}`}
										aria-checked={isSelected}
										disabled={(answered && !isSelected) || disabled}
										onPress={() => {
											if (!(answered || disabled)) {
												handleSelectChoice();
											}
										}}
									/>
								)}
							</Box>
						) : null
					}
					onPress={() => handleSelectChoice()}
					titleNumberOfLines={10}
					title={() => (
						<Box>
							{media?.length ? (
								<ImagesCarousel
									images={media}
									useResponsiveImageWidthWeb={false}
									containerStyle={{
										flexDirection: "row",
										alignItems: "center",
										justifyContent: "flex-start",
									}}
									customImageWidth={120}
									customImageHeight={120}
									maxSingleImageContainerWidth={120}
								/>
							) : null}
							<RenderMarkdown
								text={text}
								variant="answer"
								fontSize={fontSize}
								customStyles={{
									text: {
										color:
											answered && isCorrectAnswer
												? theme.palette.success.dark
												: answered && isSelected
													? theme.colors.onErrorContainer
													: theme.colors.text,
										...(answered && (isCorrectAnswer || isSelected)
											? { fontFamily: `${appFont}-semibold` }
											: {}),
										...(isCrossedOut
											? {
													opacity: 0.38,
													textDecorationLine: "line-through",
												}
											: {}),
									},
								}}
							/>
						</Box>
					)}
					right={(p) => (
						<Box
							{...p}
							height={34}
							minWidth={60}
							flexDirection="row"
							justifyContent="flex-end"
							alignItems="center"
							alignSelf={showStatChip ? "flex-start" : undefined}
						>
							{showStatChip ? (
								<PercentageAnsweredChip value={percentageAnswered!} />
							) : null}
							{!answered && showCrossOut && !drag && (
								<CrossOutAnswerIconButton {...p} isCrossedOut={isCrossedOut} />
							)}
							{drag && <DragStatusIcon />}
						</Box>
					)}
					testID={`choice-list-item-${index}`}
				/>
			</ListItemContainer>
		</Card>
	);
};

export default ChoiceListItem;
